import {
  generalBusinessItemTemplate,
  generalBusinessItemController
} from '../modals';

import moment from 'moment';

class GeneralBusinessDetailsController {

  constructor(CompanyService, toastr, $scope, $http, $rootScope, $stateParams, $state, $uibModal, TravelExpenseService) {
    'ngInject';

    this.moment = moment;
    this.$http = $http;
    this.$rootScope = $rootScope;
    this.apiHost = $rootScope.apiHost;
    this.toastr = toastr;
    this.state = $state;
    this.$stateParams = $stateParams;
    this.$uibModal = $uibModal;
    this.travelExpenseService = TravelExpenseService;

    this.report = { expenses: [] };
    this.statusOptions = {
      buttonText: 'Submit for Approval',
      nextStatus: 'SUBMITTED'
    };
    this.travelExpenseStatusDetails = {};
    this.baselineFingerprint = null;
    this.hasSavedChanges = false;

    this.loading = true;
    this.travelExpenseService.getExpenseReport($stateParams.expenseReportId).then((response) => {
      this.loading = false;
      const report = this.travelExpenseService.normalizeExpenseReportDates(response.data);
      this.travelExpenseService.hydrateEstimateBasisDisplay(report);
      this.report = report;
      this.report.expenses = report.expenses || [];
      this.travelExpenseStatusDetails = this.travelExpenseService.getTravelExpenseStatusDetails(this.report.status);
      this.captureBaseline();
    }, () => {
      this.loading = false;
      this.toastr.error('Could not load the expense report.', 'Error');
    });

    $scope.$watch(() => this.travelExpenseStatusDetails, (newVal) => {
      if (!newVal) {
        return;
      }

      if (newVal.value === 'APPROVED') {
        this.statusOptions.buttonText = 'Submit for Reimbursement';
        this.statusOptions.nextStatus = 'REIMBURSEMENT_REQUESTED';
      } else if (newVal.value === 'REIMBURSEMENT_REJECTED') {
        this.statusOptions.buttonText = 'Resubmit for Reimbursement';
        this.statusOptions.nextStatus = 'REIMBURSEMENT_REQUESTED';
      } else if (newVal.value === 'REJECTED') {
        this.statusOptions.buttonText = 'Resubmit for Approval';
        this.statusOptions.nextStatus = 'SUBMITTED';
      } else {
        this.statusOptions.buttonText = 'Submit for Approval';
        this.statusOptions.nextStatus = 'SUBMITTED';
      }
    });
  }

  getItems() {
    return (this.report.expenses || []).filter((expense) => !expense.itinerary);
  }

  getItemsTotal() {
    return this.getItems().reduce((total, expense) => total + (expense.amount || 0), 0);
  }

  hasItems() {
    return this.getItems().length > 0;
  }

  formatDate(value) {
    return value ? moment(value).format('MMM DD, YYYY') : '';
  }

  isRejected() {
    const status = this.report && this.report.status;
    return status === 'REJECTED' || status === 'REIMBURSEMENT_REJECTED';
  }

  isReimbursementRejected() {
    return this.report && this.report.status === 'REIMBURSEMENT_REJECTED';
  }

  isLocked() {
    if (this.travelExpenseService.isProxyImportEditable(this.report)) {
      return false;
    }
    return !this.travelExpenseStatusDetails.editable;
  }

  captureBaseline() {
    this.baselineFingerprint = this.getReportFingerprint(this.report);
    this.hasSavedChanges = false;
  }

  getReportFingerprint(report) {
    if (!report) {
      return '';
    }

    const expenses = this.getItems().map((expense) => ({
      id: expense.id || null,
      type: expense.type || '',
      description: (expense.description || '').trim(),
      amount: Number(expense.amount || 0),
      dateNeeded: expense.dateNeeded ? this.moment(expense.dateNeeded).format('YYYY-MM-DD') : null,
      documentationLink: (expense.documentationLink || '').trim(),
      hasPendingFile: !!expense.estimateBasisFile,
      pendingReceiptDeletes: (expense.pendingReceiptIdsToDelete || []).slice().sort()
    }));

    return JSON.stringify({
      name: (report.name || '').trim(),
      description: (report.description || '').trim(),
      expenses
    });
  }

  isDirty() {
    if (!this.baselineFingerprint) {
      return false;
    }

    return this.getReportFingerprint(this.report) !== this.baselineFingerprint;
  }

  hasPositiveEstimate() {
    return this.getItemsTotal() > 0;
  }

  canSave() {
    if (this.isLocked()) {
      return false;
    }

    return this.isDirty();
  }

  canResubmit() {
    if (!this.isRejected()) {
      return this.hasPositiveEstimate() && this.hasItems();
    }

    if (!this.isDirty() && !this.hasSavedChanges) {
      return false;
    }

    return this.hasPositiveEstimate() && this.hasItems();
  }

  addItem() {
    this.openItemModal(null);
  }

  editItem(expense) {
    if (this.isLocked()) {
      return;
    }
    this.openItemModal(expense);
  }

  openItemModal(expenseToEdit) {
    const self = this;
    const modalInstance = this.$uibModal.open({
      template: generalBusinessItemTemplate,
      controller: generalBusinessItemController,
      controllerAs: '$ctrl',
      resolve: {
        existingExpense() {
          return expenseToEdit || null;
        }
      },
      windowClass: 'hmodal-success'
    });

    modalInstance.addItemToReport = function(expense) {
      self.report.expenses.push(expense);
    };

    modalInstance.updateItemInReport = function(original, updated) {
      self.applyItemEdits(original, updated);
    };
  }

  applyItemEdits(original, updated) {
    const expenses = this.report.expenses || [];
    const index = expenses.indexOf(original);

    if (index === -1) {
      return;
    }

    const target = expenses[index];
    target.type = updated.type;
    target.description = updated.description;
    target.amount = updated.amount;
    target.dateNeeded = updated.dateNeeded;
    target.documentationLink = updated.documentationLink;
    target.estimateBasisFile = updated.estimateBasisFile;
    target.estimateBasisFileName = updated.estimateBasisFileName;

    if (updated.estimateBasisFile) {
      target.pendingReceiptIdsToDelete = (target.receipts || [])
        .map((receipt) => receipt.receipt || receipt.id)
        .filter(Boolean);
      target.receipts = [];
    }
  }

  removeItem(index) {
    const items = this.getItems();
    const target = items[index];
    const expenses = this.report.expenses || [];
    const actualIndex = expenses.indexOf(target);
    if (actualIndex !== -1) {
      expenses.splice(actualIndex, 1);
    }
  }

  cancel() {
    this.state.go('expenses');
  }

  saveExpenseReport() {
    if (!this.canSave()) {
      this.toastr.warning('Make changes before saving.', 'No Changes');
      return;
    }

    const localReport = this.report;
    const payload = this.travelExpenseService.serializeExpenseReport(this.report);
    const hasPendingUploads = (localReport.expenses || []).some((expense) => expense.estimateBasisFile);
    const stayOnPage = this.isRejected();
    const savingRejectedChanges = stayOnPage && this.isDirty();

    delete payload.status;
    this.loading = true;
    this.$http.post(
      this.$rootScope.apiHost + '/api/expense-reports/' + this.report.id,
      payload,
      { withCredentials: true }
    ).then((success) => {
      const finishSave = (savedReport) => {
        this.loading = false;

        if (stayOnPage) {
          const report = this.travelExpenseService.normalizeExpenseReportDates(savedReport || success.data);
          this.travelExpenseService.hydrateEstimateBasisDisplay(report);
          this.report = report;
          this.report.expenses = report.expenses || [];
          this.captureBaseline();
          this.hasSavedChanges = savingRejectedChanges;
          this.toastr.success(
            savingRejectedChanges
              ? 'Changes saved. You can now resubmit for approval.'
              : 'Expense report saved.',
            'Saved'
          );
          return;
        }

        this.toastr.success('Expense report saved.', 'Saved');
        this.state.go('expenses');
      };

      if (!hasPendingUploads) {
        finishSave(success.data);
        return;
      }

      this.travelExpenseService.uploadPendingEstimateBasisFiles(localReport, success.data).then((refreshed) => {
        finishSave(refreshed || success.data);
      }, () => {
        this.loading = false;
        this.toastr.warning('The expense report was saved, but one or more documents could not be uploaded.', 'Upload Warning');
        if (stayOnPage) {
          this.captureBaseline();
          this.hasSavedChanges = savingRejectedChanges;
        } else {
          this.state.go('expenses');
        }
      });
    }, (error) => {
      this.loading = false;
      const message = error && error.data && error.data.message
        ? error.data.message
        : 'Could not save the expense report.';
      this.toastr.error(message, 'Save Failed');
    });
  }

  submitExpenseReport() {
    if (this.isRejected() && !this.canResubmit()) {
      this.toastr.warning('Update the request before resubmitting it for approval.', 'No Changes');
      return;
    }

    const submitStatus = () => {
      return this.travelExpenseService.updateExpenseReportStatus(
        this.report.id,
        this.statusOptions.nextStatus
      ).then(() => {
        this.loading = false;
        this.toastr.success(
          this.isRejected() ? 'Request resubmitted for approval.' : 'Request submitted.',
          'Submitted'
        );
        this.state.go('expenses');
      }, (error) => {
        this.loading = false;
        const message = error && error.data && error.data.message
          ? error.data.message
          : 'Could not submit the expense report.';
        this.toastr.error(message, 'Submit Failed');
      });
    };

    if (this.isRejected() && this.isDirty()) {
      const localReport = this.report;
      const payload = this.travelExpenseService.serializeExpenseReport(this.report);
      delete payload.status;
      this.loading = true;

      this.$http.post(
        this.$rootScope.apiHost + '/api/expense-reports/' + this.report.id,
        payload,
        { withCredentials: true }
      ).then((success) => {
        const hasPendingUploads = (localReport.expenses || []).some((expense) => expense.estimateBasisFile);

        if (!hasPendingUploads) {
          return submitStatus();
        }

        return this.travelExpenseService.uploadPendingEstimateBasisFiles(localReport, success.data)
          .then(() => submitStatus(), () => {
            this.loading = false;
            this.toastr.warning('Changes were saved, but one or more documents could not be uploaded. Fix uploads before resubmitting.', 'Upload Warning');
          });
      }, (error) => {
        this.loading = false;
        const message = error && error.data && error.data.message
          ? error.data.message
          : 'Could not save changes before resubmitting.';
        this.toastr.error(message, 'Submit Failed');
      });
      return;
    }

    this.loading = true;
    submitStatus();
  }
}

export default GeneralBusinessDetailsController;



// WEBPACK FOOTER //
// ./client/pages/expenses/general-business-details/general-business-details.controller.js